import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { leadsService } from '../../services/leads';

export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (params, { rejectWithValue }) => {
    try {
      const response = await leadsService.getLeads(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch leads');
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/createLead',
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await leadsService.createLead(leadData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create lead');
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ id, leadData }, { rejectWithValue }) => {
    try {
      const response = await leadsService.updateLead(id, leadData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update lead');
    }
  }
);

export const assignLead = createAsyncThunk(
  'leads/assignLead',
  async ({ leadId, assignedTo, reason }, { rejectWithValue }) => {
    try {
      const assignData = { assigned_to: assignedTo, reason };
      const response = await leadsService.assignLead(leadId, assignData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to assign lead');
    }
  }
);

export const fetchLeadById = createAsyncThunk(
  'leads/fetchLeadById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await leadsService.getLeadById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch lead');
    }
  }
);

export const addLeadComment = createAsyncThunk(
  'leads/addLeadComment',
  async ({ leadId, comment }, { rejectWithValue }) => {
    try {
      const response = await leadsService.addLeadComment(leadId, comment);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to add comment');
    }
  }
);

export const fetchLeadsAnalytics = createAsyncThunk(
  'leads/fetchLeadsAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await leadsService.getLeadsAnalytics();
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch analytics');
    }
  }
);

export const fetchFollowUpLeads = createAsyncThunk(
  'leads/fetchFollowUpLeads',
  async (date, { rejectWithValue }) => {
    try {
      const response = await leadsService.getFollowUpLeads(date);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch follow-up leads');
    }
  }
);

export const bulkAssignLeads = createAsyncThunk(
  'leads/bulkAssignLeads',
  async (assignData, { rejectWithValue }) => {
    try {
      const response = await leadsService.bulkAssignLeads(assignData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to bulk assign leads');
    }
  }
);

const leadsSlice = createSlice({
  name: 'leads',
  initialState: {
    leads: [],
    currentLead: null,
    analytics: null,
    followUpLeads: [],
    loading: false,
    error: null,
    pagination: {
      count: 0,
      page: 1,
      pageSize: 20,
    },
  },
  reducers: {
    setCurrentLead: (state, action) => {
      state.currentLead = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    updateLeadInList: (state, action) => {
      const index = state.leads.findIndex(lead => lead.id === action.payload.id);
      if (index !== -1) {
        state.leads[index] = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        state.leads = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || action.payload.length,
          page: action.payload.page || 1,
          pageSize: action.payload.pageSize || 20,
        };
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.leads.unshift(action.payload);
      })
      .addCase(updateLead.fulfilled, (state, action) => {
        const index = state.leads.findIndex(lead => lead.id === action.payload.id);
        if (index !== -1) {
          state.leads[index] = action.payload;
        }
      })
      .addCase(fetchLeadById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeadById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload;
      })
      .addCase(fetchLeadById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(assignLead.fulfilled, (state, action) => {
        const index = state.leads.findIndex(lead => lead.id === action.payload.id);
        if (index !== -1) {
          state.leads[index] = action.payload;
        }
        if (state.currentLead && state.currentLead.id === action.payload.id) {
          state.currentLead = action.payload;
        }
      })
      .addCase(fetchLeadsAnalytics.fulfilled, (state, action) => {
        state.analytics = action.payload;
      })
      .addCase(fetchFollowUpLeads.fulfilled, (state, action) => {
        state.followUpLeads = action.payload.leads || action.payload;
      })
      .addCase(bulkAssignLeads.fulfilled, (state, action) => {
        // Refresh the leads after bulk assignment
        // This would typically trigger a refetch
      });
  },
});

export const { setCurrentLead, clearError, updateLeadInList } = leadsSlice.actions;
export default leadsSlice.reducer;